import { useAuth } from '../../auth/AuthContext';
import { Button } from '../Button';
import type { MenuItem } from '../Menu';
import { Tooltip } from '../Tooltip';
import type { QuickActionStyle } from './rowQuickActionWidth';

/**
 * A row's most-used actions, as small labelled buttons beside its kebab.
 *
 * **Captured from GRC's Documents workspace**, whose every row carries a star, a download and a
 * share before the kebab. Product call, 18 Sep 2026 — the actions a reader takes on most rows
 * of a file browser are worth one click rather than two, and a repository is used like one.
 *
 * **They are PROMOTED OUT of the kebab, never duplicated into it.** One action in two places on
 * one row is the thing this product refuses everywhere else; what changes here is which menu an
 * entry lives in, not how many places it lives in. A caller moves an entry from `rowActions` to
 * `rowQuickActions` — the same `MenuItem`, so its permission, its disabled reason and its icon
 * travel with it and cannot be restated differently.
 *
 * **Reach for it sparingly, and never for a destructive action.** The kebab exists so a row
 * does not grow a run of loose buttons — the reasoning `<ProfileCard>`'s settings cog is built
 * on — and every entry promoted here is width taken from the columns. Two or three is the
 * shape this was built for; a fourth is a signal the row is being used as a toolbar.
 *
 * **It is a `<Button variant="primary">` wearing its NAME and no icon** — product call,
 * 09 Oct 2026, replacing the 28px brand-700 glyph this drew before. Three things came with
 * that:
 *
 *   * **The name is READ, not hovered.** An icon-only control owed its name twice — an
 *     `aria-label` and a mandatory `<Tooltip>` — because a glyph tells a sighted reader
 *     nothing. A labelled button says it once, on screen, to everyone. The tooltip is now kept
 *     for the `disabledReason` ALONE: repeating a visible label into a tooltip would print the
 *     same value twice, which is the rule this product applies everywhere else.
 *   * **It clears the touch-target floor.** Reference §5 asks for ≥40px targets and the glyph
 *     was 28px square. `BUTTON_SHAPE`'s `min-h-10` IS 40px, so this is not a size that may be
 *     trimmed to win back row height — `<ShortcutButton>`'s `xs` stops at 40px for the same
 *     reason, and says so.
 *   * **`item.icon` is deliberately NOT drawn.** The entry still carries one, because the same
 *     `MenuItem` is drawn with its icon when it sits in the kebab — promoting an action must
 *     not mean restating it. Here the label alone is the control.
 *
 * **It wears the brand's PRIMARY surface, which is a departure to know about.**
 * `buttonSurface.ts` calls brand yellow the primary action and says one per view; a list
 * renders one per row. Asked for directly on 09 Oct 2026, and recorded in
 * `reference/decisions/frontend-tables.md` rather than left for somebody to read as drift.
 * It is the reason to keep promoting SPARINGLY — two or three entries, never a toolbar.
 *
 * The gutter these need is `quickActionsWidth` — a measurement, no longer a constant, because
 * two labelled buttons are not the same width.
 */
export function RowQuickActions({
  items,
  id,
  style = 'label',
}: {
  items: MenuItem[];
  id: string;
  style?: QuickActionStyle;
}) {
  const { hasPermission } = useAuth();

  /*
    Permission-filtered HERE, the same way `<Menu>` filters its own entries — an action the
    reader cannot perform is not drawn at all, rather than drawn and refused. Hide, don't
    disable: `disabledReason` is for STATE, and it still disables and still says why.
  */
  const visible = items.filter((item) => !item.permission || hasPermission(item.permission));

  if (visible.length === 0) {
    return null;
  }

  /*
    The Repository exception (`QuickActionStyle`). A glyph tells a sighted reader nothing, so
    the icon form owes its name twice: `aria-label`, and a `<Tooltip>` carrying the label —
    the disabled reason still wins over it. 32px square, the `sm` step's height, so switching
    style does not move the row.
  */
  if (style === 'icon') {
    return (
      <div className="flex shrink-0 items-center gap-1">
        {visible.map((item) => {
          const disabled = Boolean(item.disabledReason);
          const name = typeof item.label === 'string' ? item.label : undefined;

          return (
            <Tooltip key={item.key} label={item.label} disabledReason={item.disabledReason ?? null}>
              <button
                type="button"
                id={`${id}-${item.key}`}
                aria-label={name}
                disabled={disabled}
                onClick={(event) => {
                  event.stopPropagation();
                  item.onClick();
                }}
                className={[
                  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg transition focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-300',
                  disabled
                    ? 'cursor-not-allowed text-slate-300'
                    : item.destructive
                      ? 'text-slate-500 hover:bg-red-50 hover:text-red-600'
                      : // brand-700, the checkbox tick's step: the glyph is the only thing saying the control is there.
                        'text-brand-700 hover:bg-brand-50 hover:text-brand-800',
                ].join(' ')}
              >
                {item.icon}
              </button>
            </Tooltip>
          );
        })}
      </div>
    );
  }

  return (
    <div className="flex shrink-0 items-center gap-1">
      {visible.map((item) => {
        const disabled = Boolean(item.disabledReason);

        const button = (
          <Button
            id={`${id}-${item.key}`}
            variant={item.destructive ? 'danger' : 'primary'}
            size="sm"
            disabled={disabled}
            /*
              The row's own click handlers must not fire as well. A quick action sits inside a
              row that opens a record and inside the region the context menu listens on, so
              without this a download would also navigate.
            */
            onClick={(event) => {
              event.stopPropagation();
              item.onClick();
            }}
          >
            {item.label}
          </Button>
        );

        /*
          Only where there is something to say. The label is on the button, so a tooltip
          repeating it would be the same value in two places; a disabled button fires no
          pointer events, which is the one case a tooltip is still needed for.
        */
        return disabled ? (
          <Tooltip key={item.key} disabledReason={item.disabledReason ?? null}>
            {button}
          </Tooltip>
        ) : (
          <span key={item.key}>{button}</span>
        );
      })}
    </div>
  );
}
